import { FEATURE_TOUR_IDS, LAYOUT_PORTAL_ID, PAGE_HEADER_OTHER_ITEMS_WRAPPER_ID, TEST_IDS } from '@va/constants';
import { setShouldRefreshDatePicker } from '@va/dashboard/actions/ui';
import { AiChatbotButton } from '@va/dashboard/feature/ai-assistant';
import { websiteRoleSelectors } from '@va/dashboard/selectors/core';
import { isMediumDevice as isMediumDeviceSelector, isTabletDevice } from '@va/dashboard/selectors/ui';
import { FeatureCounter, StpCounter } from '@va/dashboard/shared/feature-counter';
import { DatePicker } from '@va/dashboard/shared/feature-date-picker';
import { RefreshIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { isUserLoggedInWithCustomUrl } from '@va/standalone/shared/helpers';
import { PageHeaderProps } from '@va/types/page-header';
import { UserRole } from '@va/types/website';
import { Button } from '@va/ui/design-system';
import { Portal, usePageHeaderContext } from '@va/util/components';
import { is3asApp, isWixMiniApp } from '@va/util/helpers';
import classNames from 'classnames';
import { FC, PropsWithChildren, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LayoutPortal } from './Layout';
import { TitlePortal } from './TopBar';

export const PageHeader = (props: PageHeaderProps) => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const pageHeaderRef = useRef<HTMLDivElement>(null);
  const isTablet = useSelector(isTabletDevice);
  const isMediumDevice = useSelector(isMediumDeviceSelector);
  const websiteRole = useSelector(websiteRoleSelectors.selectWebsiteRole);

  //Component will use the values from the context and fallback to props
  //The goal in time is to use only the context and portals where needed
  const { pageHeaderProps = {} } = usePageHeaderContext();
  const {
    title = props.title,
    hideDatePicker = props.hideDatePicker,
    dateEnabled = props.dateEnabled,
    enforceDateLimit = props.enforceDateLimit,
    hideFeatureCounter = props.hideFeatureCounter,
    hideAiChatbot = props.hideAiChatbot,
    refreshFn = props.refreshFn,
    className = props.className,
    hideBorder = props.hideBorder,
    otherItems = props.otherItems,
    renderCreateButton = props.renderCreateButton,
    otherItemsClassName = props.otherItemsClassName,
    rightSideClassName = props.rightSideClassName,
  } = pageHeaderProps;

  return (
    <>
      <TitlePortal>{title}</TitlePortal>
      <LayoutPortal>
        <div
          data-testid={TEST_IDS.specific.pageHeaderContainer}
          ref={pageHeaderRef}
          className={classNames(
            'z-(--zIndex-layout) flex items-center bg-white backdrop-filter backdrop-blur-md justify-between order-last lg:order-first gap-2 md:gap-18px px-2 lg:pl-30 lg:pr-18 pt-18 pb-18',
            { 'lg:border-b lg:border-gray-mercury': !hideBorder },
            className,
          )}
        >
          {(props.showTitleOnTablet || !isTablet) && title}
          <div
            className={classNames(
              'w-full lg:w-auto flex items-center flex-col-reverse lg:flex-row-reverse sm-initial:justify-between grow gap-3',
              rightSideClassName,
            )}
          >
            <div className='flex w-full items-center flex-wrap justify-end gap-2'>
              <div
                className={classNames('flex items-center gap-2 flex-wrap', otherItemsClassName)}
                id={PAGE_HEADER_OTHER_ITEMS_WRAPPER_ID}
              >
                {otherItems}
              </div>
              {renderCreateButton?.()}
              {refreshFn && (
                <Button
                  data-testid={TEST_IDS.generic.buttons.refresh}
                  icon={() => <RefreshIcon />}
                  color='secondary'
                  onClick={() => {
                    dispatch(setShouldRefreshDatePicker());
                    refreshFn();
                  }}
                  tooltip={translate('card.button.refresh')}
                  size={isMediumDevice ? 'small' : 'large'}
                />
              )}
              {!hideDatePicker && (
                <DatePicker
                  featureReleaseDate={dateEnabled}
                  enforceDateLimit={enforceDateLimit}
                  size={isMediumDevice ? 'small' : 'medium'}
                />
              )}
              {!isWixMiniApp() &&
                !hideAiChatbot &&
                (!isUserLoggedInWithCustomUrl() || ![UserRole.WATCHER, UserRole.DASHBOARD].includes(websiteRole)) && (
                  <AiChatbotButton {...(isMediumDevice ? { text: undefined, size: 'small' } : {})} />
                )}
              {is3asApp() && <div className='contents' id={FEATURE_TOUR_IDS.pageHeaderContainer} />}
            </div>
            {!is3asApp() && !hideFeatureCounter && <FeatureCounter />}
            {is3asApp() && <StpCounter />}
          </div>
        </div>
      </LayoutPortal>
    </>
  );
};

export const PageHeaderItemsWrapper: FC<PropsWithChildren<{}>> = ({ children }) => {
  return (
    <Portal
      elementSelector={`#${PAGE_HEADER_OTHER_ITEMS_WRAPPER_ID}`}
      mutationObserver={{ targetNode: `#${LAYOUT_PORTAL_ID}` }}
    >
      {children}
    </Portal>
  );
};
